// Retrying after a failure that may pass on its own (a server restarting,
// GitHub slow for a moment). The wait before each try is random, from 1 s
// up to a ceiling that doubles from 2 s and stops at 15 s ("full jitter"),
// so clients that failed together don't come back together.

const FIRST_CAP_MS = 2000;
/** At most this long between tries, so a server that is back is noticed within 15 s. */
const MAX_CAP_MS = 15_000;
const MIN_WAIT_MS = 1000;

/** The longest wait after `failures` failed tries (0 or more): 2 s, 4 s, 8 s, then 15 s. */
export function backoffCapMs(failures: number): number {
  return Math.min(FIRST_CAP_MS * 2 ** Math.max(0, failures), MAX_CAP_MS);
}

/** The wait before the next try after `failures` failed tries: random, 1 s to `backoffCapMs`. */
export function backoffMs(failures: number, random: () => number = Math.random): number {
  return Math.max(MIN_WAIT_MS, Math.round(random() * backoffCapMs(failures)));
}

/** Where a `Retrier` is: waiting until `at` (epoch ms), trying, or done (a try succeeded). */
export type RetryState = { phase: 'waiting'; at: number } | { phase: 'trying' } | { phase: 'done' };

/**
 * Tries `attempt` until it resolves true, waiting `backoffMs` between
 * failed tries (a try that throws has failed). `retryNow` tries at once,
 * unless a try is already running; `retryIfDue` only when the next try is
 * due (a hidden tab's timers fire late). The backoff carries on from
 * there. Every change is reported to `onChange`. After `stop`, or once a
 * try has succeeded, it does nothing more.
 */
export class Retrier {
  private failures = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private dueAt: number | null = null;
  private trying = false;
  private stopped = false;

  constructor(
    private attempt: () => Promise<boolean>,
    private onChange: (state: RetryState) => void,
    private random: () => number = Math.random,
  ) {}

  /** Waits for the first try. */
  start() {
    this.schedule();
  }

  retryNow() {
    if (this.stopped || this.trying) return;
    this.clear();
    void this.run();
  }

  retryIfDue() {
    if (this.dueAt !== null && Date.now() >= this.dueAt) this.retryNow();
  }

  stop() {
    this.stopped = true;
    this.clear();
  }

  private clear() {
    if (this.timer !== null) clearTimeout(this.timer);
    this.timer = null;
    this.dueAt = null;
  }

  private schedule() {
    if (this.stopped) return;
    const wait = backoffMs(this.failures, this.random);
    this.dueAt = Date.now() + wait;
    this.timer = setTimeout(() => void this.run(), wait);
    this.onChange({ phase: 'waiting', at: this.dueAt });
  }

  private async run() {
    this.clear();
    this.trying = true;
    this.onChange({ phase: 'trying' });
    let ok = false;
    try {
      ok = await this.attempt();
    } catch {
      ok = false;
    }
    this.trying = false;
    if (this.stopped) return;
    if (ok) {
      this.stopped = true;
      this.onChange({ phase: 'done' });
      return;
    }
    this.failures++;
    this.schedule();
  }
}
